import React from 'react';
import { Helmet } from "react-helmet";

const TermsConditions = () => {
  return (
    <>
      <Helmet>
        <title>Tanarela - Premium Landscape Design Materials Australia</title>
        <meta name="description" content="Tanarela - Premium landscape design materials and natural stone products for creating stunning outdoor spaces in Australia" />
      </Helmet>

      <div className="policy-container">
      <div className="policy-content">
        <h1>Terms and Conditions</h1>
        <p className="last-updated">Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>

        <section>
          <h2>1. Introduction and Acceptance</h2>
          <p>
            Welcome to Tanarela. These Terms and Conditions ("Terms") govern your access to and use of the Tanarela website 
            (tanarela.com), including the purchase of our premium landscape design materials and natural stone products 
            ("Products" or "Services").
          </p>
          <p>
            By accessing or using our website, creating an account, or placing an order, you agree to be bound by these Terms 
            and our Privacy Policy. If you do not agree with any part of these Terms, you must not use our website or services.
          </p>
          <p>
            These Terms constitute a legally binding agreement between you ("you," "your," or "User") and Tanarela 
            ("we," "our," "us," or "Company"). Please read these Terms carefully before using our services.
          </p>
        </section>

        <section>
          <h2>2. Eligibility and Account Registration</h2>
          
          <h3>2.1 Age Requirement</h3>
          <p>
            You must be at least 18 years of age to use our website and purchase our products. By using our services, 
            you represent and warrant that you meet this age requirement and have the legal capacity to enter into these Terms.
          </p>

          <h3>2.2 Account Registration</h3>
          <p>
            To access certain features and make purchases, you may need to create an account. When registering, you agree to:
          </p>
          <ul>
            <li>Provide accurate, current, and complete information</li>
            <li>Maintain and promptly update your account information</li>
            <li>Maintain the security and confidentiality of your login credentials</li>
            <li>Accept responsibility for all activities that occur under your account</li>
            <li>Notify us immediately of any unauthorized use or security breach</li>
          </ul>

          <h3>2.3 Account Termination</h3>
          <p>
            We reserve the right to suspend or terminate your account at our discretion, without prior notice, if we believe 
            you have violated these Terms or engaged in fraudulent, illegal, or harmful activities.
          </p>
        </section>

        <section>
          <h2>3. Products and Services</h2>
          
          <h3>3.1 Product Descriptions</h3>
          <p>
            We strive to provide accurate descriptions, specifications, and images of our landscape design materials and 
            natural stone products. However, we do not warrant that product descriptions, colors, specifications, or other 
            content are accurate, complete, reliable, current, or error-free.
          </p>
          <p>
            Natural stone products may vary in color, texture, pattern, and appearance due to their organic nature. 
            Images are for illustrative purposes and may not represent exact product variations.
          </p>

          <h3>3.2 Product Availability</h3>
          <p>
            All products are subject to availability. We reserve the right to discontinue any product at any time without notice. 
            In the event a product is unavailable after an order is placed, we will notify you and offer a refund or suitable alternative.
          </p>

          <h3>3.3 Pricing</h3>
          <p>
            All prices are displayed in Australian Dollars (AUD) and include or exclude GST as indicated. We reserve the right 
            to change prices at any time without prior notice. Price changes will not affect orders that have already been confirmed.
          </p>
          <p>
            In the event of a pricing error, we reserve the right to cancel or refuse any orders placed at the incorrect price, 
            even after an order confirmation has been sent.
          </p>

          <h3>3.4 Product Suitability</h3>
          <p>
            You are responsible for ensuring that the products you purchase are suitable for your intended use and project requirements. 
            We recommend consulting with landscape design professionals before making significant purchases. We are not liable for 
            products that do not meet your specific project needs.
          </p>
        </section>

        <section>
          <h2>4. Orders and Payment</h2>
          
          <h3>4.1 Order Process</h3>
          <p>
            When you place an order through our website, you are making an offer to purchase products subject to these Terms. 
            All orders are subject to acceptance and availability. We reserve the right to refuse or cancel any order for any reason, 
            including but not limited to:
          </p>
          <ul>
            <li>Product unavailability or stock limitations</li>
            <li>Pricing or product description errors</li>
            <li>Suspected fraudulent or unauthorized transactions</li>
            <li>Failure to meet eligibility requirements</li>
            <li>Restrictions on quantities or bulk orders</li>
          </ul>

          <h3>4.2 Order Confirmation</h3>
          <p>
            After placing an order, you will receive an email confirmation acknowledging receipt of your order. This confirmation 
            does not constitute acceptance of your order. Acceptance occurs when we ship the products or send a shipping confirmation email.
          </p>

          <h3>4.3 Payment Methods</h3>
          <p>
            We accept various payment methods as displayed during checkout, including credit cards, debit cards, and other 
            electronic payment methods. Payment must be received in full before products are shipped.
          </p>

          <h3>4.4 Payment Security</h3>
          <p>
            All payment transactions are processed securely through trusted third-party payment processors. We do not store 
            your complete credit card information on our servers. You agree to provide valid payment information and authorize 
            us to charge the total amount of your order to your selected payment method.
          </p>

          <h3>4.5 Payment Authorization</h3>
          <p>
            By providing payment information, you represent and warrant that you are authorized to use the payment method and 
            authorize us to charge all fees incurred by you. You agree to pay all charges at the prices in effect when incurred, 
            including applicable taxes and shipping fees.
          </p>
        </section>

        <section>
          <h2>5. Shipping and Delivery</h2>
          
          <h3>5.1 Shipping Locations</h3>
          <p>
            We currently ship to locations within Australia. Shipping options, costs, and estimated delivery times will be 
            displayed during checkout and may vary based on your location and the size/weight of your order.
          </p>

          <h3>5.2 Delivery Times</h3>
          <p>
            Estimated delivery times are approximate and not guaranteed. We are not responsible for delays caused by shipping 
            carriers, customs clearance, weather conditions, or other factors beyond our control.
          </p>

          <h3>5.3 Shipping Costs</h3>
          <p>
            Shipping costs are calculated based on order weight, dimensions, destination, and selected shipping method. 
            You will see the total shipping cost before completing your purchase.
          </p>

          <h3>5.4 Delivery and Inspection</h3>
          <p>
            Upon delivery, you should inspect the products immediately for any visible damage or discrepancies. If you notice 
            any issues, please note them on the delivery receipt and contact us within 48 hours. Failure to report damage within 
            this timeframe may affect your ability to file a claim.
          </p>

          <h3>5.5 Undeliverable Orders</h3>
          <p>
            If an order is returned to us as undeliverable due to an incorrect address provided by you or failure to accept 
            delivery, you may be responsible for return shipping costs and reshipment fees.
          </p>
        </section>

        <section>
          <h2>6. Returns and Refunds</h2>
          <p>
            Please refer to our separate Refund Policy for detailed information about returns, refunds, exchanges, and our 
            satisfaction guarantee. The Refund Policy is incorporated into these Terms by reference.
          </p>
        </section>

        <section>
          <h2>7. Intellectual Property Rights</h2>
          
          <h3>7.1 Ownership</h3>
          <p>
            All content on the Tanarela website, including but not limited to text, graphics, logos, images, product descriptions, 
            photographs, videos, software, and design elements, is the property of Tanarela or its licensors and is protected by 
            Australian and international copyright, trademark, and other intellectual property laws.
          </p>

          <h3>7.2 Limited License</h3>
          <p>
            We grant you a limited, non-exclusive, non-transferable, revocable license to access and use our website for personal, 
            non-commercial purposes in accordance with these Terms. This license does not include:
          </p>
          <ul>
            <li>Resale or commercial use of the website or its content</li>
            <li>Modification, reproduction, or distribution of website content</li>
            <li>Use of data mining, robots, or similar data gathering tools</li>
            <li>Downloading (except page caching) or copying account information for the benefit of another party</li>
            <li>Framing or linking to the website without authorization</li>
          </ul>

          <h3>7.3 Trademarks</h3>
          <p>
            Tanarela and associated logos, product names, and service names are trademarks or registered trademarks of Tanarela. 
            You may not use these trademarks without our prior written permission.
          </p>

          <h3>7.4 User Content</h3>
          <p>
            If you submit reviews, comments, photos, or other content to our website, you grant us a non-exclusive, royalty-free, 
            perpetual, worldwide license to use, reproduce, modify, publish, and display such content for business purposes.
          </p>
        </section>

        <section>
          <h2>8. Prohibited Activities</h2>
          <p>You agree not to engage in any of the following prohibited activities:</p>
          <ul>
            <li>Violating any applicable laws, regulations, or third-party rights</li>
            <li>Using the website for fraudulent or unlawful purposes</li>
            <li>Impersonating any person or entity or misrepresenting your affiliation</li>
            <li>Interfering with or disrupting the website or servers</li>
            <li>Attempting to gain unauthorized access to any portion of the website</li>
            <li>Using automated systems (bots, scrapers) to access the website without permission</li>
            <li>Transmitting viruses, malware, or other harmful code</li>
            <li>Collecting or harvesting user information without consent</li>
            <li>Engaging in any activity that could damage, disable, or impair the website</li>
            <li>Reverse engineering or attempting to extract source code</li>
            <li>Removing or modifying copyright or proprietary notices</li>
          </ul>
        </section>

        <section>
          <h2>9. Limitation of Liability</h2>
          
          <h3>9.1 Disclaimer of Warranties</h3>
          <p>
            Our website and products are provided "as is" and "as available" without warranties of any kind, either express or implied, 
            including but not limited to implied warranties of merchantability, fitness for a particular purpose, or non-infringement.
          </p>
          <p>
            We do not warrant that the website will be uninterrupted, secure, or error-free, or that defects will be corrected. 
            We do not warrant the accuracy, completeness, or reliability of any content or information on the website.
          </p>

          <h3>9.2 Limitation of Liability</h3>
          <p>
            To the maximum extent permitted by law, Tanarela, its directors, employees, affiliates, and suppliers shall not be 
            liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to:
          </p>
          <ul>
            <li>Loss of profits, revenue, data, or business opportunities</li>
            <li>Personal injury or property damage</li>
            <li>Service interruptions or data loss</li>
            <li>Cost of substitute products or services</li>
            <li>Errors or omissions in content</li>
          </ul>
          <p>
            Our total liability to you for any claims arising from or related to these Terms or your use of the website shall not 
            exceed the amount you paid to us in the twelve (12) months preceding the claim, or AUD $100, whichever is greater.
          </p>

          <h3>9.3 Australian Consumer Law</h3>
          <p>
            Nothing in these Terms excludes, restricts, or modifies any consumer rights under the Australian Consumer Law or other 
            applicable laws that cannot be excluded, restricted, or modified by agreement. Our liability for breach of any such 
            non-excludable consumer guarantee is limited to, at our option:
          </p>
          <ul>
            <li>In the case of goods: replacement, repair, or payment of the cost of replacement or repair</li>
            <li>In the case of services: re-supply of the services or payment of the cost of re-supply</li>
          </ul>
        </section>

        <section>
          <h2>10. Indemnification</h2>
          <p>
            You agree to indemnify, defend, and hold harmless Tanarela, its affiliates, officers, directors, employees, agents, 
            and suppliers from and against any claims, liabilities, damages, losses, costs, expenses (including reasonable legal fees) 
            arising out of or related to:
          </p>
          <ul>
            <li>Your use or misuse of the website or products</li>
            <li>Your violation of these Terms</li>
            <li>Your violation of any rights of third parties</li>
            <li>Your violation of any applicable laws or regulations</li>
            <li>Any content you submit or transmit through the website</li>
          </ul>
        </section>

        <section>
          <h2>11. Dispute Resolution</h2>
          
          <h3>11.1 Governing Law</h3>
          <p>
            These Terms shall be governed by and construed in accordance with the laws of Australia, without regard to its 
            conflict of law provisions.
          </p>

          <h3>11.2 Jurisdiction</h3>
          <p>
            You agree that any legal action or proceeding arising out of or related to these Terms or your use of the website 
            shall be brought exclusively in the courts of Australia, and you consent to the personal jurisdiction of such courts.
          </p>

          <h3>11.3 Informal Resolution</h3>
          <p>
            Before filing a formal claim, we encourage you to contact us to seek an informal resolution. We are committed to 
            working with you to resolve disputes fairly and efficiently.
          </p>
        </section>

        <section>
          <h2>12. Modifications to Terms</h2>
          <p>
            We reserve the right to modify or update these Terms at any time at our sole discretion. Changes will be effective 
            immediately upon posting to the website with an updated "Last Updated" date. Your continued use of the website after 
            changes are posted constitutes your acceptance of the modified Terms.
          </p>
          <p>
            We encourage you to review these Terms periodically to stay informed of any updates. Material changes may be 
            communicated through email or a prominent notice on our website.
          </p>
        </section>

        <section>
          <h2>13. Termination</h2>
          <p>
            We may terminate or suspend your access to the website immediately, without prior notice or liability, for any reason, 
            including but not limited to breach of these Terms. Upon termination, your right to use the website will immediately cease.
          </p>
          <p>
            Termination does not relieve you of obligations incurred prior to termination. Sections of these Terms that by their 
            nature should survive termination (including warranty disclaimers, liability limitations, and dispute resolution provisions) 
            shall remain in effect.
          </p>
        </section>

        <section>
          <h2>14. Severability</h2>
          <p>
            If any provision of these Terms is found to be invalid, illegal, or unenforceable by a court of competent jurisdiction, 
            such provision shall be modified to reflect the parties' intention to the maximum extent permitted by law, or if it 
            cannot be modified, it shall be severed from these Terms. The remaining provisions shall continue in full force and effect.
          </p>
        </section>

        <section>
          <h2>15. Entire Agreement</h2>
          <p>
            These Terms, together with our Privacy Policy and Refund Policy, constitute the entire agreement between you and 
            Tanarela regarding your use of the website and supersede all prior or contemporaneous communications, agreements, 
            and understandings, whether written or oral.
          </p>
        </section>

        <section>
          <h2>16. Waiver</h2>
          <p>
            Our failure to enforce any right or provision of these Terms shall not constitute a waiver of such right or provision. 
            No waiver of any term shall be deemed a further or continuing waiver of such term or any other term.
          </p>
        </section>

        <section>
          <h2>17. Assignment</h2>
          <p>
            You may not assign or transfer these Terms or your rights and obligations hereunder without our prior written consent. 
            We may assign or transfer these Terms without restriction or notification.
          </p>
        </section>

        <section>
          <h2>18. Force Majeure</h2>
          <p>
            We shall not be liable for any failure or delay in performance due to circumstances beyond our reasonable control, 
            including but not limited to acts of God, natural disasters, war, terrorism, labor disputes, government actions, 
            pandemics, or supplier failures.
          </p>
        </section>

        <section>
          <h2>19. Contact Information</h2>
          <p>
            If you have any questions, concerns, or feedback regarding these Terms and Conditions, please contact us:
          </p>
          <div className="contact-info">
            <p><strong>Tanarela</strong></p>
            <p>Email: <a href="mailto:tanarela@hotmail.com">tanarela@hotmail.com</a></p>
            <p>Website: https://tanarela.com</p>
            <p>Address: Australia</p>
          </div>
          <p>
            We are committed to addressing your inquiries and will respond within a reasonable timeframe.
          </p>
        </section>

        <section>
          <h2>20. Acknowledgment</h2>
          <p>
            By using our website and services, you acknowledge that you have read, understood, and agree to be bound by these 
            Terms and Conditions. If you do not agree with any part of these Terms, you must discontinue use of our website immediately.
          </p>
        </section>
      </div>
    </div>
  
    </>);
};

export default TermsConditions;